/**
 * Professionals List - Storefront API Version
 * 
 * @version 3.0.0
 * @description Scalable professionals listing with Storefront API pagination
 * 
 * Features:
 * - Storefront API with cursor-based pagination (scales to 1000+ items)
 * - Server-side filtering via GraphQL
 * - Search on: name, city, province, category
 * - URL state management
 * - Loading states & error handling
 * - Fallback to server-rendered content
 * - Accessibility (ARIA, keyboard navigation)
 * 
 * Requirements:
 * - Storefront API access token (set in theme settings or section)
 * - Metaobject published to Storefront API
 */

(function() {
  'use strict';

  // =============================================================================
  // Configuration
  // =============================================================================

  const CONFIG = {
    apiVersion: '2026-04',
    defaultItemsPerPage: 12,
    debounceDelay: 350,
    scrollOffset: 100,
    retryAttempts: 2,
    retryDelay: 1000
  };

  // =============================================================================
  // GraphQL Queries
  // =============================================================================

  const QUERIES = {
    // Main query to fetch professionals with pagination
    professionals: `
      query GetProfessionals($type: String!, $first: Int!, $after: String) {
        metaobjects(type: $type, first: $first, after: $after) {
          edges {
            cursor
            node {
              id
              handle
              type
              fields {
                key
                value
                type
                reference {
                  ... on MediaImage {
                    image {
                      url
                      altText
                      width
                      height
                    }
                  }
                  ... on Metaobject {
                    id
                    handle
                    type
                    fields {
                      key
                      value
                    }
                  }
                  ... on Product {
                    id
                    handle
                    title
                    featuredImage {
                      url
                      altText
                      width
                      height
                    }
                    onlineStoreUrl
                  }
                }
                references(first: 10) {
                  edges {
                    node {
                      ... on Metaobject {
                        id
                        handle
                        type
                        fields {
                          key
                          value
                        }
                      }
                      ... on Product {
                        id
                        handle
                        title
                        featuredImage {
                          url
                          altText
                          width
                          height
                        }
                        onlineStoreUrl
                      }
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    `,

    // Query to fetch all categories for filters
    categories: `
      query GetCategories($type: String!) {
        metaobjects(type: $type, first: 100) {
          edges {
            node {
              id
              handle
              fields {
                key
                value
              }
            }
          }
        }
      }
    `
  };

  // =============================================================================
  // Utility Functions
  // =============================================================================

  /**
   * Debounce function execution
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Parse metaobject fields into a usable object
   */
  function parseMetaobjectFields(fields) {
    const result = {};
    
    if (!fields || !Array.isArray(fields)) return result;

    fields.forEach(field => {
      const { key, value, type, reference, references } = field;
      
      // Handle different field types
      if (reference) {
        result[key] = reference;
      } else if (references?.edges?.length > 0) {
        result[key] = references.edges.map(e => e.node);
      } else if (value) {
        // Try to parse JSON values
        try {
          result[key] = JSON.parse(value);
        } catch {
          result[key] = value;
        }
      }
    });

    return result;
  }

  /**
   * Get category name from category object
   */
  function getCategoryName(category) {
    if (!category) return '';
    
    if (typeof category === 'string') return category;
    
    // Parse fields if it's a metaobject
    if (category.fields) {
      const parsed = parseMetaobjectFields(category.fields);
      return parsed.category_name || parsed.title || parsed.name || category.handle || '';
    }
    
    return category.category_name || category.title || category.name || category.handle || '';
  }

  /**
   * Generate image URL with size
   */
  function getImageUrl(imageObj, width = 600) {
    if (!imageObj?.url) return null;
    
    // Shopify CDN URL transformation
    const url = imageObj.url;
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d+)?(\.[^.]+)$/, `_${width}x$2`);
    }
    return url;
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Convert string to handle format (like Shopify's handleize filter)
   * This ensures consistent handle generation between JS and Liquid
   */
  function handleize(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens
  }

  /**
   * Get a URL string from a metaobject field
   */
  function getUrlField(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value.url) return value.url;
      if (value.value && value.value.url) return value.value.url;
    }
    return '';
  }

  // =============================================================================
  // API Client
  // =============================================================================

  class StorefrontAPI {
    constructor(options = {}) {
      this.shopDomain = options.shopDomain || window.Shopify?.shop || window.location.hostname;
      this.accessToken = options.accessToken;
      this.apiVersion = options.apiVersion || CONFIG.apiVersion;
      
      if (!this.accessToken) {
        console.warn('[StorefrontAPI] No access token provided. API calls will fail.');
      }
    }

    get endpoint() {
      return `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;
    }

    async query(graphqlQuery, variables = {}) {
      if (!this.accessToken) {
        throw new Error('Storefront API access token is required');
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[StorefrontAPI] HTTP Error:', response.status, errorText);
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const json = await response.json();

      if (json.errors) {
        console.error('[StorefrontAPI] GraphQL errors:', json.errors);
        // Log more details about the error
        json.errors.forEach((err, i) => {
          console.error(`[StorefrontAPI] Error ${i + 1}:`, err.message);
          if (err.locations) console.error('  Locations:', err.locations);
          if (err.path) console.error('  Path:', err.path);
        });
        throw new Error(json.errors[0]?.message || 'GraphQL query failed');
      }

      return json.data;
    }

    async fetchWithRetry(query, variables, attempts = CONFIG.retryAttempts) {
      for (let i = 0; i < attempts; i++) {
        try {
          return await this.query(query, variables);
        } catch (error) {
          if (i === attempts - 1) throw error;
          await new Promise(r => setTimeout(r, CONFIG.retryDelay * (i + 1)));
        }
      }
    }
  }

  // =============================================================================
  // Data Manager - Handles fetching and caching
  // =============================================================================

  class ProfessionalsDataManager {
    constructor(api, config = {}) {
      this.api = api;
      this.metaobjectType = config.metaobjectType || 'professional';
      this.categoryType = config.categoryType || 'category';
      this.itemsPerPage = config.itemsPerPage || CONFIG.defaultItemsPerPage;
      
      // Cache
      this.allProfessionals = [];
      this.categories = [];
      this.isFullyLoaded = false;
      this.cursors = { start: null, end: null };
    }

    /**
     * Fetch all professionals (for client-side filtering)
     * This loads all data upfront for fast filtering
     */
    async fetchAll() {
      if (this.isFullyLoaded) {
        return this.allProfessionals;
      }
      const allItems = await this.fetchAllProgressive({
        seedItems: [],
        after: null,
        hasMore: true
      });

      this.allProfessionals = allItems;
      this.isFullyLoaded = true;

      return allItems;
    }

    /**
     * Fetch all professionals progressively in batches
     * Allows seeding with an already-fetched first page to avoid duplicate request
     */
    async fetchAllProgressive({ seedItems = [], after = null, hasMore = true } = {}) {
      const allItems = Array.isArray(seedItems) ? [...seedItems] : [];
      let cursor = after;
      let continueLoading = hasMore;

      while (continueLoading) {
        const data = await this.api.fetchWithRetry(QUERIES.professionals, {
          type: this.metaobjectType,
          first: 50, // Fetch in batches of 50
          after: cursor
        });

        const edges = data.metaobjects?.edges || [];
        const pageInfo = data.metaobjects?.pageInfo;

        edges.forEach(edge => {
          allItems.push(this.transformProfessional(edge.node));
        });

        continueLoading = pageInfo?.hasNextPage || false;
        cursor = pageInfo?.endCursor || null;
      }

      return allItems;
    }

    /**
     * Fetch a single page of professionals
     */
    async fetchPage(cursor = null) {
      const data = await this.api.fetchWithRetry(QUERIES.professionals, {
        type: this.metaobjectType,
        first: this.itemsPerPage,
        after: cursor
      });

      const edges = data.metaobjects?.edges || [];
      const pageInfo = data.metaobjects?.pageInfo;

      return {
        professionals: edges.map(edge => this.transformProfessional(edge.node)),
        pageInfo,
        cursors: edges.map(e => e.cursor)
      };
    }

    /**
     * Fetch categories for filters
     */
    async fetchCategories() {
      if (this.categories.length > 0) {
        return this.categories;
      }

      try {
        const data = await this.api.fetchWithRetry(QUERIES.categories, {
          type: this.categoryType
        });

        this.categories = (data.metaobjects?.edges || []).map(edge => {
          const fields = parseMetaobjectFields(edge.node.fields);
          return {
            id: edge.node.id,
            handle: edge.node.handle,
            name: fields.category_name || fields.title || fields.name || edge.node.handle
          };
        });

        return this.categories;
      } catch (error) {
        console.warn('[ProfessionalsDataManager] Failed to fetch categories:', error);
        return [];
      }
    }

    /**
     * Transform raw metaobject into usable professional object
     */
    transformProfessional(node) {
      const fields = parseMetaobjectFields(node.fields);
      
      // Extract categories
      const categories = [];
      if (fields.category) {
        const cats = Array.isArray(fields.category) ? fields.category : [fields.category];
        cats.forEach(cat => {
          const name = getCategoryName(cat);
          if (name) {
            // Use handleize for consistent handle generation (matches Liquid's handleize filter)
            const catHandle = cat.handle || handleize(name);
            categories.push({
              id: cat.id || catHandle,
              handle: catHandle,
              name: name
            });
          }
        });
      }

      // Extract projects (products)
      const projects = [];
      if (fields.projects) {
        const prods = Array.isArray(fields.projects) ? fields.projects : [fields.projects];
        prods.forEach(prod => {
          if (prod && prod.title) {
            projects.push({
              id: prod.id,
              handle: prod.handle,
              title: prod.title,
              url: prod.onlineStoreUrl || `/products/${prod.handle}`,
              image: prod.featuredImage ? {
                url: getImageUrl(prod.featuredImage, 800),
                alt: prod.featuredImage.altText || prod.title
              } : null
            });
          }
        });
      }

      // Get featured image
      let featuredImage = null;
      if (fields.featured_image?.image) {
        featuredImage = {
          url: getImageUrl(fields.featured_image.image, 600),
          alt: fields.featured_image.image.altText || fields.title || ''
        };
      }

      // Build the URL - metaobject templates use /pages/[type]/[handle] format
      // Shopify metaobject URLs are typically: /pages/professional/[handle]
      // Or can be custom: /a/[type]/[handle]
      const url = fields.url || `/pages/professional/${node.handle}`;

      const emailUrl = getUrlField(fields.email);
      const phoneUrl = getUrlField(fields.phone_number);

      return {
        id: node.id,
        handle: node.handle,
        title: fields.title || '',
        name: fields.title || fields.name || '',
        city: fields.city || '',
        province: fields.province || '',
        categories: categories,
        categoryNames: categories.map(c => c.name).join(', '),
        projects: projects,
        featuredImage: featuredImage,
        url: url,
        email: emailUrl,
        phoneNumber: phoneUrl
      };
    }

    /**
     * Filter professionals locally
     */
    filterProfessionals(professionals, { search = '', filter = 'all' }) {
      return professionals.filter(prof => {
        // Category filter
        if (filter !== 'all') {
          // Use consistent handleize for matching
          const categoryHandles = prof.categories.map(c => c.handle);
          if (!categoryHandles.includes(filter)) {
            return false;
          }
        }

        // Search filter
        if (search) {
          const searchLower = search.toLowerCase();
          const searchTerms = searchLower.split(/\s+/).filter(Boolean);
          
          const searchableText = [
            prof.name,
            prof.title,
            prof.city,
            prof.province,
            prof.categoryNames
          ].join(' ').toLowerCase();

          // All search terms must match
          return searchTerms.every(term => searchableText.includes(term));
        }

        return true;
      });
    }

    /**
     * Get paginated results
     */
    getPaginatedResults(professionals, page, itemsPerPage) {
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      
      return {
        items: professionals.slice(startIndex, endIndex),
        totalItems: professionals.length,
        totalPages: Math.ceil(professionals.length / itemsPerPage),
        currentPage: page,
        hasNextPage: endIndex < professionals.length,
        hasPreviousPage: page > 1
      };
    }
  }

  // =============================================================================
  // HTML Renderer
  // =============================================================================

  class ProfessionalsRenderer {
    constructor(config = {}) {
      this.config = {
        bannerButtonLabel: config.bannerButtonLabel || 'Bekijk profiel',
        projectButtonLabel: config.projectButtonLabel || 'Bekijk project',
        placeholderImage: config.placeholderImage || null,
        iconEmailUrl: config.iconEmailUrl || '',
        iconPhoneUrl: config.iconPhoneUrl || '',
        ...config
      };
    }

    /**
     * Render a single professional row
     */
    renderProfessionalRow(professional, index = 0) {
      const categoryTags = professional.categories.map(cat => 
        `<span class="professional-toprow__category">${escapeHtml(cat.name)}</span>`
      ).join('');

      const categoryIds = professional.categories.map(c => 
        c.handle || c.name.toLowerCase().replace(/\s+/g, '-')
      ).join(',');

      const projectsHtml = this.renderProjects(professional.projects, professional);

      const imageHtml = professional.featuredImage
        ? `<a href="${escapeHtml(professional.url)}" class="professional-banner__link" data-instant>
            <img 
              src="${professional.featuredImage.url}" 
              alt="${escapeHtml(professional.featuredImage.alt || professional.name)}"
              class="professional-banner__image"
              loading="${index < 3 ? 'eager' : 'lazy'}"
              width="600"
              height="800"
            />
          </a>`
        : `<a href="${escapeHtml(professional.url)}" class="professional-banner__link" data-instant>
            <div class="professional-banner__image placeholder-svg">
              <svg viewBox="0 0 525 525" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="525" height="525" fill="#F5F5F5"/>
                <path d="M262.5 180C223.28 180 191.25 212.03 191.25 251.25C191.25 290.47 223.28 322.5 262.5 322.5C301.72 322.5 333.75 290.47 333.75 251.25C333.75 212.03 301.72 180 262.5 180Z" fill="#E0E0E0"/>
              </svg>
            </div>
          </a>`;

      const emailIcon = professional.email
        ? `<a href="${escapeHtml(professional.email)}?cc=info@obly.com" class="professional-action-icon" aria-label="${escapeHtml(professional.email)}?cc=info@obly.com">
            <img class="professional-action-image" src="${escapeHtml(this.config.iconEmailUrl)}" alt="E-mail ${escapeHtml(professional.name)}">
          </a>`
        : '';

      const phoneIcon = professional.phoneNumber
        ? `<a href="${escapeHtml(professional.phoneNumber)}" class="professional-action-icon" aria-label="${escapeHtml(professional.phoneNumber)}">
            <img class="professional-action-image" src="${escapeHtml(this.config.iconPhoneUrl)}" alt="Bel ${escapeHtml(professional.name)}">
          </a>`
        : '';

      return `
        <div 
          class="professional-row" 
          data-professional-id="${escapeHtml(professional.handle)}"
          data-name="${escapeHtml(professional.name)}"
          data-city="${escapeHtml(professional.city)}"
          data-province="${escapeHtml(professional.province)}"
          data-categories="${escapeHtml(categoryIds)}"
          data-category-names="${escapeHtml(professional.categoryNames.toLowerCase())}"
          role="listitem"
          style="--animation-delay: ${index * 50}ms"
        >
          <div class="professional-toprow">
            <div class="professional-toprow__left">
              <h3 class="professional-toprow__name h3">${escapeHtml(professional.name)}</h3>
              ${professional.city ? `<p class="professional-toprow__meta">${escapeHtml(professional.city)}</p>` : ''}
              ${categoryTags ? `<div class="professional-toprow__categories">${categoryTags}</div>` : ''}
            </div>
            <div class="professional-toprow__right professional-actions">
              ${emailIcon}
              ${phoneIcon}
              <a href="${escapeHtml(professional.url)}" class="button" data-instant>
                ${escapeHtml(this.config.bannerButtonLabel)}
              </a>
            </div>
          </div>
          <div class="professional-banner">
            ${imageHtml}
          </div>
          <div class="professional-projects">
            ${projectsHtml}
          </div>
        </div>
      `;
    }

    /**
     * Render projects (products) for a professional
     */
    renderProjects(projects, professional) {
      if (!projects || projects.length === 0) {
        // Return placeholders
        return Array(3).fill(null).map(() => `
          <div class="custom-card custom-card--placeholder">
            <svg viewBox="0 0 525 525" fill="none" xmlns="http://www.w3.org/2000/svg" class="custom-card__image placeholder-svg">
              <rect width="525" height="525" fill="#F5F5F5"/>
            </svg>
          </div>
        `).join('');
      }

      return projects.slice(0, 3).map((project, i) => {
        const imageHtml = project.image
          ? `<img 
              src="${project.image.url}" 
              alt="${escapeHtml(project.image.alt || project.title)}"
              class="custom-card__image"
              loading="lazy"
            />`
          : `<svg viewBox="0 0 525 525" fill="none" xmlns="http://www.w3.org/2000/svg" class="custom-card__image placeholder-svg">
              <rect width="525" height="525" fill="#F5F5F5"/>
            </svg>`;

        return `
          <custom-card class="custom-card" handle="${escapeHtml(project.handle)}">
            <div class="custom-card__figure">
              <div class="custom-card__media">
                ${imageHtml}
              </div>
            </div>
            <div class="custom-card__info">
              <div class="custom-card__info-wrapper">
                <div class="custom-card__info-content">
                  <h5 class="product-title custom-card__product-title">${escapeHtml(project.title)}</h5>
                  <a href="${escapeHtml(project.url)}" class="button button--primary" data-instant>
                    ${escapeHtml(this.config.projectButtonLabel)}
                  </a>
                </div>
              </div>
            </div>
          </custom-card>
        `;
      }).join('');
    }

    /**
     * Render the list of professionals
     */
    renderList(professionals, startIndex = 0) {
      if (professionals.length === 0) {
        return '';
      }

      return professionals.map((prof, i) => 
        this.renderProfessionalRow(prof, startIndex + i)
      ).join('');
    }

    /**
     * Render pagination
     */
    renderPagination(currentPage, totalPages) {
      if (totalPages <= 1) return '';

      const buttons = this.getPaginationRange(currentPage, totalPages);

      const pagesHtml = buttons.map(item => {
        if (item === '...') {
          return '<span class="professionals-pagination__ellipsis" aria-hidden="true">…</span>';
        }
        return `
          <button 
            type="button" 
            class="professionals-pagination__btn${item === currentPage ? ' is-active' : ''}"
            data-page="${item}"
            aria-label="Pagina ${item}"
            ${item === currentPage ? 'aria-current="page"' : ''}
          >${item}</button>
        `;
      }).join('');

      return pagesHtml;
    }

    getPaginationRange(current, total) {
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const pages = [1];

      if (current > 3) pages.push('...');

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (current < total - 2) pages.push('...');

      if (total > 1) pages.push(total);

      return pages;
    }
  }

  // =============================================================================
  // Main Controller
  // =============================================================================

  class ProfessionalsList {
    constructor(section, options = {}) {
      if (!section || !(section instanceof Element)) {
        console.warn('[ProfessionalsList] Invalid section element');
        return;
      }

      // Check for existing instance and destroy it
      if (section._professionalsListInstance) {
        console.info('[ProfessionalsList] Destroying existing instance before re-initialization');
        section._professionalsListInstance.destroy();
      }

      this.section = section;
      this.sectionId = section.id;
      this.options = Object.assign({
        collapseFiltersOnInteractionMobile: true,
        scrollBehavior: 'smooth'
      }, options);

      // Store instance reference on the section
      section._professionalsListInstance = this;

      // Check for API token (can be on section or inner container)
      const innerContainer = section.querySelector('[data-storefront-token]');
      this.accessToken = options.accessToken || 
                        section.dataset.storefrontToken ||
                        innerContainer?.dataset.storefrontToken ||
                        window.PROFESSIONALS_CONFIG?.storefrontToken;

      // Determine mode based on token availability
      this.useAPI = !!this.accessToken;
      
      // Store reference to the container with data attributes
      this.configContainer = innerContainer || section;

      if (!this.useAPI) {
        console.info('[ProfessionalsList] No Storefront API token found. Using fallback client-side mode.');
        this.initFallbackMode();
        return;
      }

      this.init();
    }

    async init() {
      try {
        this.cacheElements();
        this.initializeState();
        this.initializeComponents();
        this.bindEvents();

        // Show loading state
        this.setLoading(true);

        // Fetch initial data
        await this.loadData();

        // Render initial view
        this.render();

        // Mark as initialized
        this.section.classList.add('is-initialized', 'api-mode');
        this.emit('ready', { mode: 'api', itemCount: this.dataManager.allProfessionals.length });

      } catch (error) {
        console.error('[ProfessionalsList] Initialization failed:', error);
        this.handleError(error);
      }
    }

    /**
     * Fallback to client-side mode (using existing DOM)
     */
    initFallbackMode() {
      // Use the existing client-side implementation
      if (window.ProfessionalsList && this !== window.ProfessionalsList) {
        console.info('[ProfessionalsList] Falling back to client-side mode');
      }
      this.section.classList.add('is-initialized', 'fallback-mode');
    }

    cacheElements() {
      this.elements = {
        list: this.section.querySelector('[data-professionals-list]'),
        searchForm: this.section.querySelector('[data-professionals-search-form]'),
        searchInput: this.section.querySelector('[data-professionals-search]'),
        filterContainer: this.section.querySelector('[data-professionals-filters]'),
        filterButtons: this.section.querySelectorAll('[data-filter]'),
        pagination: this.section.querySelector('[data-professionals-pagination]'),
        pagesContainer: this.section.querySelector('[data-pagination-pages]'),
        prevBtn: this.section.querySelector('[data-pagination-prev]'),
        nextBtn: this.section.querySelector('[data-pagination-next]'),
        noResults: this.section.querySelector('[data-professionals-no-results]'),
        loadingOverlay: this.section.querySelector('[data-loading]'),
        filterAccordion: this.section.querySelector('.accordion__disclosure')
      };

      // Create loading overlay if not exists
      if (!this.elements.loadingOverlay) {
        const overlay = document.createElement('div');
        overlay.className = 'professionals-loading';
        overlay.setAttribute('data-loading', '');
        overlay.innerHTML = '<div class="professionals-loading__spinner"></div>';
        this.elements.list?.parentNode?.insertBefore(overlay, this.elements.list);
        this.elements.loadingOverlay = overlay;
      }
    }

    initializeState() {
      // Read from URL
      const params = new URLSearchParams(window.location.search);

      this.state = {
        page: Math.max(1, parseInt(params.get('page')) || 1),
        filter: params.get('filter') || 'all',
        search: params.get('q') || '',
        isLoading: false
      };

      this.isPartialData = false;
      this.loadRequestId = 0;
      this.isDestroyed = false;

      // Get config from section/container
      this.itemsPerPage = parseInt(this.elements.list?.dataset.itemsPerPage) || CONFIG.defaultItemsPerPage;
      this.metaobjectType = this.configContainer.dataset.metaobjectType || 'professional';
      this.categoryType = this.configContainer.dataset.categoryType || 'category';
    }

    initializeComponents() {
      // API Client
      this.api = new StorefrontAPI({
        accessToken: this.accessToken,
        apiVersion: CONFIG.apiVersion
      });

      // Data Manager
      this.dataManager = new ProfessionalsDataManager(this.api, {
        metaobjectType: this.metaobjectType,
        categoryType: this.categoryType,
        itemsPerPage: this.itemsPerPage
      });

      // Renderer
      this.renderer = new ProfessionalsRenderer({
        bannerButtonLabel: this.configContainer.dataset.bannerButtonLabel || 'Bekijk profiel',
        projectButtonLabel: this.configContainer.dataset.projectButtonLabel || 'Bekijk project',
        iconEmailUrl: this.configContainer.dataset.iconEmailUrl || '',
        iconPhoneUrl: this.configContainer.dataset.iconPhoneUrl || ''
      });
    }

    async loadData() {
      try {
        const requestId = ++this.loadRequestId;
        const firstPage = await this.dataManager.fetchPage(null);
        const firstItems = firstPage.professionals || [];

        // If no data from API, fall back to client-side mode
        if (!firstItems.length) {
          console.warn('[ProfessionalsList] No professionals from API, using fallback');
          this.initClientSideFallback();
          return;
        }

        // Render with a fast first page while remaining pages load in background
        this.dataManager.allProfessionals = firstItems;
        this.dataManager.isFullyLoaded = false;
        this.isPartialData = true;

        // Load categories in parallel; do not block first-page paint
        this.dataManager.fetchCategories().then(categories => {
          if (requestId !== this.loadRequestId || this.isDestroyed) return;
          if (categories.length > 0 && this.elements.filterContainer) {
            this.updateFilterButtons(categories);
          }
        }).catch(error => {
          console.warn('[ProfessionalsList] Failed to fetch categories:', error);
        });

        // Load the rest in background, seeded with first page data
        this.dataManager.fetchAllProgressive({
          seedItems: firstItems,
          after: firstPage.pageInfo?.endCursor || null,
          hasMore: firstPage.pageInfo?.hasNextPage || false
        }).then(allItems => {
          if (requestId !== this.loadRequestId || this.isDestroyed) return;
          const canKeepCurrentListVisible =
            !this.useFallbackMode &&
            this.state.page === 1 &&
            this.state.filter === 'all' &&
            !this.state.search;

          this.dataManager.allProfessionals = allItems;
          this.dataManager.isFullyLoaded = true;
          this.isPartialData = false;

          if (canKeepCurrentListVisible) {
            // Avoid list re-render flicker when the user is still on default page-1 view.
            this.syncMetaUIWithoutListRerender();
          } else {
            this.render();
          }

          this.emit('updated', { mode: 'api', itemCount: allItems.length });
          console.info(`[ProfessionalsList] Background load complete: ${allItems.length} professionals`);
        }).catch(error => {
          if (requestId !== this.loadRequestId || this.isDestroyed) return;
          console.warn('[ProfessionalsList] Background batch load failed:', error);
          // Recover from partial mode so UI does not remain stuck with hidden pagination.
          this.isPartialData = false;
          this.syncMetaUIWithoutListRerender();
          this.emit('error', { error, phase: 'background-load' });
        });

        console.info(`[ProfessionalsList] Initial page loaded quickly: ${firstItems.length} professionals`);
      } catch (error) {
        console.error('[ProfessionalsList] API error:', error.message);
        throw error; // Re-throw to trigger handleError
      }
    }

    updateFilterButtons(categories) {
      // Keep the "All" button, update/add category buttons
      const existingAll = this.elements.filterContainer.querySelector('[data-filter="all"]');
      
      if (existingAll) {
        // Clear and rebuild
        const allBtn = existingAll.cloneNode(true);
        this.elements.filterContainer.innerHTML = '';
        this.elements.filterContainer.appendChild(allBtn);

        categories.forEach(cat => {
          // Use consistent handleize for filter value
          const catHandle = cat.handle || handleize(cat.name);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'professionals-filter-btn';
          btn.dataset.filter = catHandle;
          btn.textContent = cat.name;
          btn.setAttribute('aria-pressed', 'false');
          this.elements.filterContainer.appendChild(btn);
        });

        // Re-cache filter buttons
        this.elements.filterButtons = this.section.querySelectorAll('[data-filter]');

        // Re-bind events (this will clone buttons to remove old listeners)
        this.bindFilterEvents();

        // Sync UI with current state
        this.syncUIWithState();
      }
    }

    isMobile() {
      return window.matchMedia && window.matchMedia('(max-width: 991px)').matches;
    }

    collapseFiltersIfNeeded() {
      if (!this.options.collapseFiltersOnInteractionMobile) return;
      if (!this.isMobile()) return;
      const details = this.elements.filterAccordion;
      if (details && details.tagName === 'DETAILS' && details.open) {
        // Close via <summary> click to keep accordion-disclosure internal state in sync
        const summary = details.querySelector('summary');
        if (summary) {
          summary.click();
        } else {
          details.open = false;
        }
        return true;
      }
    }

    scrollToResults() {
      if (!this.isMobile()) return;
      const hasNoResults = !!(this.elements.noResults && !this.elements.noResults.hidden);
      const isLoading = !!(this.elements.loadingOverlay && this.elements.loadingOverlay.classList.contains('is-visible'));
      const listIsEmpty = !!(this.elements.list && this.elements.list.children.length === 0);

      const target = hasNoResults
        ? this.elements.noResults
        : (isLoading || listIsEmpty) ? this.elements.loadingOverlay : this.elements.list;
      if (!target) return;

      const offset = CONFIG.scrollOffset ?? 100;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: this.options.scrollBehavior || 'smooth' });
    }

    scrollToResultsAfterLayoutSettle({ wasCollapsed = false } = {}) {
      const delay = wasCollapsed ? 250 : 0;
      window.setTimeout(() => {
        requestAnimationFrame(() => this.scrollToResults());
      }, delay);
    }

    bindEvents() {
      // Search
      if (this.elements.searchInput) {
        const debouncedSearch = debounce((value) => {
          this.state.search = value;
          this.state.page = 1;
          this.render();
          this.updateURL();
        }, CONFIG.debounceDelay);

        this.elements.searchInput.addEventListener('input', (e) => {
          debouncedSearch(e.target.value.trim());
        });

        this.elements.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.elements.searchInput.value = '';
            debouncedSearch('');
          }
        });

        // Set initial value from URL
        if (this.state.search) {
          this.elements.searchInput.value = this.state.search;
        }
      }

      // Search submit (mobile UX): collapse/scroll only on submit
      if (this.elements.searchForm && this.elements.searchInput) {
        this.elements.searchForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const value = this.elements.searchInput.value.trim();
          const shouldShowLoading = this.isPartialData && (value || this.state.filter !== 'all');

          if (this.state.search !== value) {
            this.state.search = value;
            this.state.page = 1;
            this.render(shouldShowLoading);
            this.updateURL();
          }

          const wasCollapsed = !!this.collapseFiltersIfNeeded();
          this.scrollToResultsAfterLayoutSettle({ wasCollapsed });
        });
      }

      // Filters
      this.bindFilterEvents();

      // Pagination
      if (this.elements.prevBtn) {
        this.elements.prevBtn.addEventListener('click', () => this.goToPage(this.state.page - 1));
      }
      if (this.elements.nextBtn) {
        this.elements.nextBtn.addEventListener('click', () => this.goToPage(this.state.page + 1));
      }

      // Pagination page buttons (delegated)
      if (this.elements.pagesContainer) {
        this.elements.pagesContainer.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-page]');
          if (btn) {
            this.goToPage(parseInt(btn.dataset.page));
          }
        });
      }

      // Browser history - store reference for cleanup
      this.handlePopState = () => {
        this.readStateFromURL();
        this.syncUIWithState();
        this.render(false);
      };
      window.addEventListener('popstate', this.handlePopState);

      // Keyboard navigation
      if (this.elements.pagination) {
        this.elements.pagination.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.goToPage(this.state.page - 1);
          if (e.key === 'ArrowRight') this.goToPage(this.state.page + 1);
        });
      }
    }

    bindFilterEvents() {
      // Remove existing listeners first to prevent duplicates
      this.elements.filterButtons.forEach(btn => {
        // Clone and replace to remove all event listeners
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
      });
      
      // Re-cache the buttons after cloning
      this.elements.filterButtons = this.section.querySelectorAll('[data-filter]');
      
      // Now add fresh event listeners
      this.elements.filterButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const filter = btn.dataset.filter;
          if (this.state.filter === filter) return;

          this.state.filter = filter;
          this.state.page = 1;

          // Update UI
          this.elements.filterButtons.forEach(b => {
            b.classList.toggle('is-active', b.dataset.filter === filter);
            b.setAttribute('aria-pressed', b.dataset.filter === filter ? 'true' : 'false');
          });

          const shouldShowLoading = this.isPartialData && (this.state.search || filter !== 'all');
          this.render(shouldShowLoading);
          this.updateURL();
          const wasCollapsed = !!this.collapseFiltersIfNeeded();
          this.scrollToResultsAfterLayoutSettle({ wasCollapsed });
        });
      });
    }

    readStateFromURL() {
      const params = new URLSearchParams(window.location.search);
      this.state.page = Math.max(1, parseInt(params.get('page')) || 1);
      this.state.filter = params.get('filter') || 'all';
      this.state.search = params.get('q') || '';
    }

    syncUIWithState() {
      // Search input
      if (this.elements.searchInput) {
        this.elements.searchInput.value = this.state.search;
      }

      // Filter buttons
      this.elements.filterButtons.forEach(btn => {
        const isActive = btn.dataset.filter === this.state.filter;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    updateURL() {
      const params = new URLSearchParams();

      if (this.state.page > 1) params.set('page', this.state.page);
      if (this.state.filter !== 'all') params.set('filter', this.state.filter);
      if (this.state.search) params.set('q', this.state.search);

      const newURL = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

      window.history.pushState({ ...this.state }, '', newURL);
    }

    goToPage(page, scroll = true) {
      const totalPages = this.getTotalPages();
      const newPage = Math.max(1, Math.min(page, totalPages || 1));

      if (newPage === this.state.page) return;

      this.state.page = newPage;
      this.render();
      this.updateURL();

      if (scroll) {
        const offset = CONFIG.scrollOffset;
        const top = this.elements.list.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }

    getTotalPages() {
      const filtered = this.getFilteredProfessionals();
      return Math.ceil(filtered.length / this.itemsPerPage);
    }

    getFilteredProfessionals() {
      // Use fallback rows if in fallback mode
      if (this.useFallbackMode && this.fallbackRows) {
        return this.filterFallbackRows();
      }
      
      return this.dataManager.filterProfessionals(
        this.dataManager.allProfessionals,
        { search: this.state.search, filter: this.state.filter }
      );
    }

    /**
     * Filter fallback rows (client-side filtering)
     */
    filterFallbackRows() {
      const searchTerms = this.state.search.toLowerCase().split(/\s+/).filter(Boolean);
      const filterValue = this.state.filter.toLowerCase();

      return this.fallbackRows.filter(row => {
        // Category filter
        if (filterValue !== 'all') {
          if (!row.categories.includes(filterValue)) {
            return false;
          }
        }

        // Search filter
        if (searchTerms.length > 0) {
          const searchableText = `${row.name} ${row.city} ${row.province} ${row.categoryNames}`;
          return searchTerms.every(term => searchableText.includes(term));
        }

        return true;
      });
    }

    /**
     * Update pagination/no-results/loading state without replacing the visible list DOM.
     * Used to avoid flicker when only metadata changes.
     */
    syncMetaUIWithoutListRerender() {
      const filtered = this.getFilteredProfessionals();
      const totalItems = filtered.length;
      const totalPages = Math.ceil(totalItems / this.itemsPerPage);
      const endIndex = this.state.page * this.itemsPerPage;

      if (this.elements.noResults) {
        const show = totalItems === 0;
        this.elements.noResults.classList.toggle('is-visible', show);
        this.elements.noResults.hidden = !show;
      }

      this.renderPagination({
        totalItems,
        totalPages,
        currentPage: this.state.page,
        hasNextPage: endIndex < totalItems,
        hasPreviousPage: this.state.page > 1
      });
      this.setLoading(false);
    }

    render(showLoading = false) {
      if (showLoading) this.setLoading(true);

      const filtered = this.getFilteredProfessionals();
      
      // Calculate pagination
      const totalItems = filtered.length;
      const totalPages = Math.ceil(totalItems / this.itemsPerPage);

      // When only the first batch is loaded, keep requested page in state (for deep links like ?page=5),
      // but render page 1 temporarily until full data is available.
      const effectivePage = this.isPartialData ? 1 : this.state.page;

      // Clamp page only when full data is available
      if (!this.isPartialData && this.state.page > totalPages && totalPages > 0) {
        this.state.page = totalPages;
      }

      const startIndex = (effectivePage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;

      requestAnimationFrame(() => {
        if (this.useFallbackMode) {
          // Fallback mode: show/hide existing DOM elements
          this.fallbackRows.forEach(row => {
            row.element.hidden = true;
            row.element.setAttribute('aria-hidden', 'true');
          });

          filtered.slice(startIndex, endIndex).forEach((row, i) => {
            row.element.hidden = false;
            row.element.setAttribute('aria-hidden', 'false');
            row.element.style.setProperty('--animation-delay', `${i * 50}ms`);
          });
        } else {
          // API mode: render new HTML
          const visibleItems = filtered.slice(startIndex, endIndex);
          
          if (this.elements.list) {
            this.elements.list.innerHTML = this.renderer.renderList(visibleItems);
          }
        }

        // No results
        if (this.elements.noResults) {
          // Avoid no-results flash while background batches are still loading
          const show = totalItems === 0 && !this.isPartialData;
          this.elements.noResults.classList.toggle('is-visible', show);
          this.elements.noResults.hidden = !show;
        }

        // Pagination
        const result = {
          totalItems,
          totalPages,
          currentPage: this.isPartialData ? effectivePage : this.state.page,
          hasNextPage: endIndex < totalItems,
          hasPreviousPage: (this.isPartialData ? effectivePage : this.state.page) > 1
        };
        this.renderPagination(result);

        // Hide loading (but keep it visible while background batches are loading and user has an active search/filter,
        // so we don't scroll to an empty list with no feedback).
        const keepLoadingVisible = this.isPartialData && (this.state.search || this.state.filter !== 'all');
        this.setLoading(keepLoadingVisible ? true : false);
      });
    }

    renderPagination(result) {
      if (!this.elements.pagination) return;

      if (this.isPartialData) {
        this.elements.pagination.hidden = true;
        return;
      }

      const show = result.totalPages > 1;
      this.elements.pagination.hidden = !show;

      if (!show) return;

      // Pages
      if (this.elements.pagesContainer) {
        this.elements.pagesContainer.innerHTML = this.renderer.renderPagination(
          result.currentPage,
          result.totalPages
        );
      }

      // Prev/Next
      if (this.elements.prevBtn) {
        this.elements.prevBtn.disabled = !result.hasPreviousPage;
      }
      if (this.elements.nextBtn) {
        this.elements.nextBtn.disabled = !result.hasNextPage;
      }
    }

    setLoading(isLoading) {
      this.state.isLoading = isLoading;
      
      if (this.elements.loadingOverlay) {
        this.elements.loadingOverlay.classList.toggle('is-visible', isLoading);
      }
      
      this.section.classList.toggle('is-loading', isLoading);
    }

    handleError(error) {
      console.error('[ProfessionalsList] Error:', error);
      
      // Hide loading
      this.setLoading(false);
      
      // Mark as error state
      this.section.classList.add('api-error');
      this.section.classList.remove('api-mode');
      
      // IMPORTANT: Don't clear the list - keep server-rendered content visible
      // The server-rendered HTML is already in the DOM, so users can still see it
      
      // Show all server-rendered rows (they might be hidden)
      if (this.elements.list) {
        this.elements.list.querySelectorAll('.professional-row').forEach(row => {
          row.hidden = false;
          row.removeAttribute('aria-hidden');
        });
      }
      
      // Initialize client-side fallback for the existing content
      console.info('[ProfessionalsList] Falling back to client-side mode due to API error');
      this.initClientSideFallback();
      
      this.emit('error', { error });
    }

    /**
     * Initialize client-side mode using existing DOM content
     * This is used as a fallback when API fails
     */
    initClientSideFallback() {
      // Cache existing rows for filtering
      const rows = this.elements.list?.querySelectorAll('.professional-row') || [];
      
      this.fallbackRows = Array.from(rows).map((row, index) => ({
        element: row,
        index: index,
        name: (row.dataset.name || '').toLowerCase().trim(),
        city: (row.dataset.city || '').toLowerCase().trim(),
        province: (row.dataset.province || '').toLowerCase().trim(),
        categories: (row.dataset.categories || '')
          .split(',')
          .map(c => c.trim().toLowerCase())
          .filter(Boolean),
        categoryNames: (row.dataset.categoryNames || '').toLowerCase().trim()
      }));

      // Re-render using fallback data
      this.useFallbackMode = true;
      this.render();
    }

    emit(eventName, detail = {}) {
      this.section.dispatchEvent(new CustomEvent(`professionals:${eventName}`, {
        bubbles: true,
        detail: { instance: this, ...detail }
      }));
    }

    // Public API
    getState() {
      return { ...this.state };
    }

    getStats() {
      return {
        totalItems: this.dataManager.allProfessionals.length,
        filteredItems: this.getFilteredProfessionals().length,
        currentPage: this.state.page,
        totalPages: this.getTotalPages(),
        mode: this.useAPI ? 'api' : 'fallback'
      };
    }

    refresh() {
      this.loadRequestId++;
      this.isPartialData = false;
      this.dataManager.isFullyLoaded = false;
      this.dataManager.allProfessionals = [];
      this.loadData().then(() => this.render());
    }

    /**
     * Destroy the instance and clean up
     */
    destroy() {
      this.isDestroyed = true;

      // Remove popstate listener
      if (this.handlePopState) {
        window.removeEventListener('popstate', this.handlePopState);
      }

      // Clear any pending timers
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Remove initialization markers
      this.section.classList.remove('is-initialized', 'api-mode', 'fallback-mode', 'api-error');
      delete this.section.dataset.apiInitialized;

      // Clear instance reference
      if (this.section._professionalsListInstance === this) {
        delete this.section._professionalsListInstance;
      }

      console.info('[ProfessionalsList] Instance destroyed');
    }
  }

  // =============================================================================
  // Auto-initialization
  // =============================================================================

  function init() {
    document.querySelectorAll('.shopify-section--professionals-list').forEach(section => {
      // Check if already initialized with API version
      if (section.dataset.apiInitialized) return;
      section.dataset.apiInitialized = 'true';

      new ProfessionalsList(section);
    });
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('shopify-section--professionals-list')) {
      // The constructor will handle destroying any existing instance
      event.target.removeAttribute('data-api-initialized');
      new ProfessionalsList(event.target);
    }
  });

  // Clean up on section unload (theme editor)
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target._professionalsListInstance) {
      event.target._professionalsListInstance.destroy();
    }
  });

  // Export
  window.ProfessionalsListAPI = ProfessionalsList;

})();